import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TNewVATReportResponsePayload,
	TVatReportFileRequestPayload,
	TVatReportFileResponsePayload,
	TVatReportFileZipResponsePayload,
	TVatReportStatusRequestPayload,
	TVatReportStatusResponsePayload,
	TVATReportStore,
	TVatReportsResponsePayload,
	TSwissPostVATReportResponsePayload,
	TEuDdpVATReportResponsePayload,
} from './types';
import {
	NEW_IOSS_FILE_TYPE_ALL,
	NEW_IOSS_FILE_TYPE_WITH_PAYMENT,
	NEW_NON_UNION_FILE_TYPE_ALL,
	NEW_NON_UNION_FILE_TYPE_WITH_PAYMENT,
	NEW_NON_UNION_XML_FILE_TYPE_ALL,
	NEW_NON_UNION_XML_FILE_TYPE_WITH_PAYMENT,
	NEW_UNION_FILE_TYPE_ALL,
	NEW_UNION_FILE_TYPE_WITH_PAYMENT,
} from './constants';
// ==========================================:
export const initialState: TVATReportStore = {
	newFile: null,
	newFileType: null,
	newFileLoader: false,
	vatReportStatus: null,
	vatReportStatusLoader: false,
	vatReportFile: null,
	vatReportFileLoader: false,
	vatReportFileZip: null,
	vatReports: null,
	vatReportsLoader: false,
	swissPostVATReport: null,
	swissPostVATReportLoader: false,
	euDdpVATReport: null,
	euDdpVATReportLoader: false,
};

// ==========================================:
const vatReport = createSlice({
	name: '@@vatReport',
	initialState,
	reducers: {
		newIOSSVATReportRequest: (state: TVATReportStore) => {
			const newFileRequestState = state;
			newFileRequestState.newFileLoader = true;
			newFileRequestState.newFileType = NEW_IOSS_FILE_TYPE_ALL;
		},
		newIOSSVATReportWithPaymentRequest: (state: TVATReportStore) => {
			const newFileRequestState = state;
			newFileRequestState.newFileLoader = true;
			newFileRequestState.newFileType = NEW_IOSS_FILE_TYPE_WITH_PAYMENT;
		},
		newNonUnionVATReportRequest: (state: TVATReportStore) => {
			const newFileRequestState = state;
			newFileRequestState.newFileLoader = true;
			newFileRequestState.newFileType = NEW_NON_UNION_FILE_TYPE_ALL;
		},
		newNonUnionVATReportWithPaymentRequest: (state: TVATReportStore) => {
			const newFileRequestState = state;
			newFileRequestState.newFileLoader = true;
			newFileRequestState.newFileType = NEW_NON_UNION_FILE_TYPE_WITH_PAYMENT;
		},
		newUnionVATReportRequest: (state: TVATReportStore) => {
			const newFileRequestState = state;
			newFileRequestState.newFileLoader = true;
			newFileRequestState.newFileType = NEW_UNION_FILE_TYPE_ALL;
		},
		newUnionVATReportWithPaymentRequest: (state: TVATReportStore) => {
			const newFileRequestState = state;
			newFileRequestState.newFileLoader = true;
			newFileRequestState.newFileType = NEW_UNION_FILE_TYPE_WITH_PAYMENT;
		},

		newNonUnionVATXmlReportRequest: (state: TVATReportStore) => {
			const newFileRequestState = state;
			newFileRequestState.newFileLoader = true;
			newFileRequestState.newFileType = NEW_NON_UNION_XML_FILE_TYPE_ALL;
		},
		newNonUnionVATXmlReportWithPaymentRequest: (state: TVATReportStore) => {
			const newFileRequestState = state;
			newFileRequestState.newFileLoader = true;
			newFileRequestState.newFileType = NEW_NON_UNION_XML_FILE_TYPE_WITH_PAYMENT;
		},

		newVATReportSuccess: (
			state: TVATReportStore,
			action: PayloadAction<TNewVATReportResponsePayload>,
		) => {
			const { payload } = action;
			const vatReportSuccessState = state;
			vatReportSuccessState.newFile = payload;
			vatReportSuccessState.newFileLoader = false;
		},
		vatReportStatusRequest: (
			state: TVATReportStore,
			action: PayloadAction<TVatReportStatusRequestPayload>,
		) => {
			const vatReportRequestState = state;
			vatReportRequestState.vatReportStatusLoader = true;
		},
		vatReportStatusSuccess: (
			state: TVATReportStore,
			action: PayloadAction<TVatReportStatusResponsePayload>,
		) => {
			const { payload } = action;
			const vatReportSuccessState = state;
			vatReportSuccessState.vatReportStatus = payload;
			vatReportSuccessState.vatReportStatusLoader = false;
		},

		vatReportFileRequest: (
			state: TVATReportStore,
			action: PayloadAction<TVatReportFileRequestPayload>,
		) => {
			const vatReportRequestState = state;
			vatReportRequestState.vatReportStatusLoader = true;
		},

		vatReportFileSuccess: (
			state: TVATReportStore,
			action: PayloadAction<TVatReportFileResponsePayload>,
		) => {
			const { payload } = action;
			const vatReportSuccessState = state;
			vatReportSuccessState.vatReportFile = payload;
			vatReportSuccessState.vatReportFileLoader = false;
		},

		vatReportFileZipRequest: (
			state: TVATReportStore,
			action: PayloadAction<TVatReportFileRequestPayload>,
		) => {
			const vatReportRequestState = state;
			vatReportRequestState.vatReportStatusLoader = true;
		},

		vatReportFileZipSuccess: (
			state: TVATReportStore,
			action: PayloadAction<TVatReportFileZipResponsePayload>,
		) => {
			const { payload } = action;
			const vatReportSuccessState = state;
			vatReportSuccessState.vatReportFileZip = payload;
			vatReportSuccessState.vatReportFileLoader = false;
		},
		getVatReportsRequest: (state: TVATReportStore) => {
			const vatReportsRequestState = state;
			vatReportsRequestState.vatReportsLoader = true;
		},
		getVatReportsSuccess: (
			state: TVATReportStore,
			action: PayloadAction<TVatReportsResponsePayload>,
		) => {
			const { payload } = action;
			const vatReportsSuccessState = state;
			vatReportsSuccessState.vatReports = payload;
			vatReportsSuccessState.vatReportsLoader = false;
		},

		swissPostVATReportRequest: (state: TVATReportStore) => {
			const swissPostVATReportRequestState = state;
			swissPostVATReportRequestState.swissPostVATReportLoader = true;
		},
		swissPostVATReportSuccess: (
			state: TVATReportStore,
			action: PayloadAction<TSwissPostVATReportResponsePayload>,
		) => {
			const { payload } = action;
			const swissPostVATReportSuccessState = state;
			swissPostVATReportSuccessState.swissPostVATReport = payload;
			swissPostVATReportSuccessState.swissPostVATReportLoader = false;
		},

		euDdpVATReportRequest: (state: TVATReportStore) => {
			const euDdpVATReportRequestState = state;
			euDdpVATReportRequestState.euDdpVATReportLoader = true;
		},
		euDdpVATReportSuccess: (
			state: TVATReportStore,
			action: PayloadAction<TEuDdpVATReportResponsePayload>,
		) => {
			const { payload } = action;
			const euDdpVATReportSuccessState = state;
			euDdpVATReportSuccessState.euDdpVATReport = payload;
			euDdpVATReportSuccessState.euDdpVATReportLoader = false;
		},
		settingsInitState: () => initialState,
	},
});

export default vatReport.reducer;
export const {
	newIOSSVATReportRequest,
	newIOSSVATReportWithPaymentRequest,
	newNonUnionVATReportRequest,
	newNonUnionVATReportWithPaymentRequest,
	newNonUnionVATXmlReportRequest,
	newNonUnionVATXmlReportWithPaymentRequest,
	newUnionVATReportRequest,
	newUnionVATReportWithPaymentRequest,
	newVATReportSuccess,
	vatReportStatusRequest,
	vatReportStatusSuccess,
	vatReportFileRequest,
	vatReportFileSuccess,
	vatReportFileZipRequest,
	vatReportFileZipSuccess,
	getVatReportsRequest,
	getVatReportsSuccess,
	swissPostVATReportRequest,
	swissPostVATReportSuccess,
	euDdpVATReportRequest,
	euDdpVATReportSuccess,
	settingsInitState,
} = vatReport.actions;
